<template>
    <div class="d-flex align-center justify-space-between mb-2">
        <travel-card-header
            :travel-card-name="travelCardName"
            :travel-card-number="formatCardNumber(number, !isNewCard)"
            :is-travel-card-blocked="isTravelCardBlocked"
        />
        <travel-card-menu
            v-if="isTravelCardMenuVisible"
            :id="id"
            :name="travelCardName"
            :number="number"
            :is-blocked="isTravelCardBlocked"
            @update:update-is-visible="handleFormVisibility('update', $event)"
            @update:block-is-visible="openDialog(CARD_DIALOG.BLOCK_CARD(id))"
            @click:delete-is-visible="openDialog(CARD_DIALOG.DELETE_CARD(id))"
        />
    </div>
    <div v-if="pendingTravelMoney?.length" data-id="travelCardDetailsNotifications" class="mb-2">
        <notification
            v-for="(pendingItem, index) in pendingTravelMoney"
            :key="index"
            :message="travelMoneyNotification(pendingItem.travelMoneyAmount, pendingItem.createdAt)"
            class="pending-travelmoney-notification"
        />
    </div>
    <div class="d-flex align-center justify-space-between pb-3">
        <update-travel-card-form
            v-model:travel-card-name="travelCardName"
            :is-re-check-needed="false"
            :is-visible="isUpdateFormVisible"
            :travel-card-id="id"
            @submit-success="handleFormVisibility('update', false)"
            @cancel="handleFormVisibility('update', false)"
        />

        <delete-travel-card-form
            :travel-card-name="travelCardName"
            :is-re-check-needed="false"
            :is-visible="true"
            :travel-card-number="number"
            :travel-card-id="id"
            :type="CARD_DIALOG.DELETE_CARD(id)"
        />

        <block-travel-card-form
            v-model:is-blocked="isTravelCardBlocked"
            :travel-card-name="travelCardName"
            :travel-card-number="number"
            :is-re-check-needed="false"
            :is-visible="true"
            :travel-card-id="id"
            :type="CARD_DIALOG.BLOCK_CARD(id)"
        />
        <template v-if="isFormNotEnabled">
            <div class="d-sm-flex d-block flex-wrap">
                <ticket-card :travel-card-number="formatCardNumber(number, !isNewCard)" :is-new-card="isNewCard" />
                <travel-card-top-up-status
                    :travel-money-amount="travelMoneyAmount"
                    :geography="autoTravelProduct?.geography"
                    :is-card-empty="isCardEmpty"
                />
            </div>
        </template>
    </div>

    <ticket-status-card :products="products" :pending-products="pendingProducts" />

    <v-card v-if="!isTravelCardBlocked" class="mt-6" elevation="0">
        <button-with-description
            data-id="buyTicketForTravelCardButton"
            :text="t('travelCard.buyTicketLabel')"
            :link="generateQuery('BuyTicketForTravelCard')"
        >
            <span class="d-block mr-2">
                {{ t('travelCard.multipleCardDescription') }}
                <tool-tip :message="t('toolTip.readMore')">
                    <span class="text-primary text-decoration-underline">({{ t('travelCard.readMore') }}).</span>
                </tool-tip>
            </span>
        </button-with-description>
        <button-with-description
            data-id="addTravelMoneyToTravelCardButton"
            :text="t('buyTicket.topUp')"
            :link="generateQuery('AddTravelMoneyToTravelCard')"
        >
            <div v-if="!travelMoneyAmount" class="mr-2 text-size-xs">
                <span class="d-block text-size-md">
                    {{ getTopUpText.before }}
                    <tool-tip :message="t('toolTip.travelMoney')">
                        <span class="text-primary text-decoration-underline">{{ t('travelCard.noTravelMoneyButton') }}</span>
                    </tool-tip>
                    {{ getTopUpText.after }}
                </span>
                {{ t('travelCard.travelAllowanceDescription') }}
            </div>
        </button-with-description>
    </v-card>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { ITravelCardDetails, IFormType } from '../../types';
import { UpdateTravelCardForm, DeleteTravelCardForm, BlockTravelCardForm } from '../form';
import TravelCardMenu from './TravelCardMenu.vue';
import TravelCardHeader from './TravelCardHeader.vue';
import ButtonWithDescription from '@/features/common/components/ButtonWithDescription.vue';
import TicketStatusCard from '../ticket/TicketStatusCard.vue';
import TicketCard from '../ticket/TicketCard.vue';
import { CARD_NUMBER_LENGTH, PURCHASE_NOTIFICATION_VISIBILITY_VALUE } from '@/constants';
import { useAppConfiguration } from '@/composables';
import { formatCardNumber, formatTimestamp, generateUrl, splitTextPattern } from '@/helpers';
import TravelCardTopUpStatus from './TravelCardTopUpStatus.vue';
import '../../styles/main.scss';
import ToolTip from '@/features/common/components/ToolTip.vue';
import { useGlobalDialogStore } from '@/store/dialogStore';
import { CARD_DIALOG } from '@/features/TravelCards/constants';
import Notification from '@/features/common/components/NotificationCard.vue';

const props = defineProps<ITravelCardDetails>();
defineEmits(['update:isBlocked', 'update:name']);

const { t } = useI18n();
const { config, language } = useAppConfiguration();
const { openDialog } = useGlobalDialogStore();

const isUpdateFormVisible = ref(false);
const travelCardName = ref(props.name);
const isTravelCardBlocked = ref(props.isBlocked);

const FORM_VISIBILITY_MAP = {
    update: isUpdateFormVisible
};

const isFormNotEnabled = computed(() => !isUpdateFormVisible.value);

const handleFormVisibility = (type: IFormType, value: boolean) => (FORM_VISIBILITY_MAP[type].value = value);

const isNewCard = props.number.length === CARD_NUMBER_LENGTH.NEW;

const isCardEmpty = computed(() => !props.products?.length && !props.pendingProducts?.length);

const isTravelCardMenuVisible = computed(() => !isTravelCardBlocked.value && isFormNotEnabled.value);

const getTopUpText = computed(() => splitTextPattern(t('travelCard.noTravelMoney'), '[LINK]'));

const splitTravelMoneyTranslation = computed(() => splitTextPattern(t('travelCard.travelMoneyOnTheWay'), '[TIME]'));

const getNotificationTime = (orderTime: Date) => formatTimestamp(orderTime, 'HH:mm', { addHours: PURCHASE_NOTIFICATION_VISIBILITY_VALUE });

const travelMoneyNotification = (travelMoneyAmount: number, createdAt: Date): string =>
    `${travelMoneyAmount} ${splitTravelMoneyTranslation.value.before} ${getNotificationTime(createdAt)} ${splitTravelMoneyTranslation.value.after}`;

const generateOrderUrl = (path: string, pathNo: string, basePath: string = config.value.webshopBaseUrl) => generateUrl(basePath, '', path);

const generateQuery = (basePath: string) =>
    generateOrderUrl(`${basePath}?travelcardId=${props.number}&travelcardName=${props.name}&language=${language.value}`, '');
</script>
